import React, { useCallback, useMemo } from 'react'
import { Autocomplete, Hint, Loading } from '@tecsinapse/cortex-react'
import { OptionSelect } from '@/core/utils/types'
import { useDebounce } from 'use-debounce'
import { FieldValues, useController, useFormContext } from 'react-hook-form'
import { ErrorMessage } from '@hookform/error-message'
import { useQuery } from '@tanstack/react-query'
import { request } from '../../core/utils/request'

interface FormSingleAutocompleteProps<T extends FieldValues> {
  name: string
  placeholder?: string
  label?: string
  onSelect?: (value: string) => void
  popoverClassName?: string
  query: string
  queryVariable?: string
  dataPath?: string
  options?: OptionSelect[]
  isQueryAutocomplete?: boolean
  optionSelectFromT?: (value: T) => OptionSelect
  filterOptions?: (value: T) => boolean
  disabled?: boolean
  allowNewOptions?: boolean
}

export const FormSingleAutocomplete = <T extends FieldValues>({
  name,
  placeholder,
  label,
  popoverClassName,
  query,
  queryVariable,
  dataPath,
  options = [],
  isQueryAutocomplete = false,
  allowNewOptions = false,
  optionSelectFromT,
  filterOptions
}: FormSingleAutocompleteProps<T>) => {
  const { setValue, control } = useFormContext()
  const {
    field: { value },
    fieldState: { error: formError },
    formState: { errors, submitCount }
  } = useController({ name, control })

  const [debouncedValue] = useDebounce(value, 1000)

  const { data, isLoading, error } = useQuery({
    queryKey: [dataPath, { debouncedValue }],
    queryFn: async () =>
      await request<Record<string, T[]>>(query, {
        variables: { [queryVariable ?? '']: debouncedValue }
      }),
    enabled: !!isQueryAutocomplete && !!debouncedValue.trim()
  })

  const filteredOptions = useMemo<OptionSelect[]>(() => {
    if (error) return []

    let optionsList: OptionSelect[]

    if (isQueryAutocomplete) {
      optionsList = (data?.[dataPath ?? ''] ?? [])
        .filter(it => (filterOptions ? filterOptions?.(it) : true))
        .map(item => optionSelectFromT?.(item))
        .filter(it => it !== undefined)
    } else {
      optionsList = (options ?? []).filter(option =>
        option.label.toLowerCase().includes(value.toLowerCase())
      )
    }

    if (
      value.trim() &&
      !optionsList.some(
        option => option.label?.toLowerCase() === value.toLowerCase()
      ) &&
      allowNewOptions
    ) {
      return [...optionsList, { label: value, value }]
    }

    return optionsList
  }, [
    data,
    dataPath,
    error,
    value,
    optionSelectFromT,
    options,
    isQueryAutocomplete,
    allowNewOptions
  ])

  const handleSelect = useCallback(
    (option: OptionSelect) => {
      setValue(name, option.value, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true
      })
    },
    [setValue]
  )

  const onChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setValue(name, event.target.value, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true
      })
    },
    [setValue]
  )

  return (
    <div className='w-full'>
      <Autocomplete.Root
        keyExtractor={(option: OptionSelect) => option.value}
        labelExtractor={(option: OptionSelect) => option.label}
      >
        <Autocomplete.Trigger
          inputValue={value}
          onChange={onChange}
          label={label}
          placeholder={placeholder}
        />
        {submitCount > 0 && formError ? (
          <ErrorMessage
            errors={errors}
            name={name}
            render={({ message }) => (
              <Hint variants={{ intent: 'error' }}>{message}</Hint>
            )}
          />
        ) : null}
        <Autocomplete.Popover className={popoverClassName}>
          <Autocomplete.Options>
            {isLoading ? (
              <div className='flex justify-center p-centi'>
                <Loading className='w-8 h-8' />
              </div>
            ) : (
              filteredOptions.map(op => (
                <Autocomplete.Option
                  option={op}
                  onSelect={handleSelect}
                  key={`${op.label}+${op.value}`}
                />
              ))
            )}
          </Autocomplete.Options>
        </Autocomplete.Popover>
      </Autocomplete.Root>
    </div>
  )
}
